import type { PersonResource } from '@mimir/web/pages/person/person-interfaces';
import { getGlobalStore, appState } from '@mimir/web/store';
import type {
  Filters,
  DefaultFilters
} from '@mimir/common/filters/Filters';
import {
  matchCustomFilters,
  getFilterQueryParams,
  setCustomFiltersToState,
  separateFiltersFromSearchString,
  serializeFilters,
} from '@mimir/common/filters/Filters';
import type {
  ParsedFilter
} from '@mimir/common/filters/filter-util';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { getViewDetailsByFieldId } from '@mimir/common/mdf/MetaDataForm';
import Vue from 'vue';
import { ViewType } from './constants';
import type { HttpQueryParameters } from '@mimir/common/util/apiClient';
import type { VueSetValue } from '@mimir/common/util/general';
import { getRouterLink } from '@mimir/client-common/util/basicRoute';
import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import { SortOptions, filterTypeForKey } from '@mimir/common/dto/searchModels';
import type { MdfEntity } from '@mimir/common/dto/mdfModels';
import { FieldType } from '@mimir/common/mdf/fieldDefinition';
import { ViewType as MdfViewType } from '@mimir/common/dto/mdfModels';
import isEmpty from 'lodash/isEmpty';
import { showToast, TOAST } from '@mimir/web/eventBus';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { exceptionSummary } from '@mimir/common/util/exception';
import { getWhiteListedParamValue, mosPersistedParams } from '@mimir/client-common/util/persistentUserSession';
import { getGlobalRouter } from '@mimir/web/router';
import { folders, setCurrentFolder } from '@mimir/web/globals/folders';
import { getMainAndSubtypeSchemas } from '@mimir/common/mdf/subtype';
import cloneDeep from 'lodash/cloneDeep';
import { invariant } from '@mimir/common/util/invariant';
import { processAndSetMdfsPromise, updateProcessAndSetMdfsPromise } from '@mimir/web/globals/variables';
import type { Route } from 'vue-router/types/router';
import { applyMultiValueOperators, describeOperatorIssue, validateOperators } from './searchBoxFilters';
import { ItemType, type SearchItemType } from '@mimir/common/dto/itemTypes';
import type { Logger } from '@mimir/common/types/logger';
import type { SearchState } from '@mimir/web/model/searchState';
import { searchState } from '@mimir/web/globals/searchState';
import { mdfState } from '@mimir/web/globals/mdfState';
import { setDefaultDateRangeField } from '@mimir/web/util/setDefaultDateRangeField';


export const padNumber = (n: number): string => {
  n = Math.floor(n);
  if (n < 10) {
    return '0' + n;
  }
  return n.toFixed(0);
};

export const msToTimeCode = (ms: number): string => {
  const seconds = (ms / 1000) % 60;
  const minutes = ((ms / (60000)) % 60);
  const hours = ms / (3600000);
  return `${padNumber(hours)}:${padNumber(minutes)}:${padNumber(seconds)}`;
};

export const addRouterLink = (persons: PersonResource[], query: HttpQueryParameters) => {
  for (const person of persons) {
    person.routerLink = {
      query,
      name: 'personDetails',
      params: {
        id: person.id,
      },
    };
  }
};

export const returnItemsWithRouterLink = (items: ClientItem[], returnTo: string, returnToItemId: string) => {
  for (const item of items) {
    const routerParams: Record<string, any> = { id: item.id };
    const routerQuery: Record<string, any> = { returnTo, returnToItemId, time: 0 };
    item.routerLink = getRouterLink(item.itemType, routerParams, routerQuery);
  }

  return items;
};

/**
 * Prepare the router query params to push to load a new search
 */
export const prepareSearchRouterQueryParams = (route?: Route): Record<string, string> => {
  const store = getGlobalStore();
  const searchParams = searchState.reactive.searchForm;
  const filters = store.state.filters;
  const allMdfs = mdfState.state.mdfs;
  const currentViewType = searchState.reactive.itemSearch.viewType;
  const includeSubfolders = store.getters.includeSubfolders as boolean;

  let finalSearchString;

  const searchString = searchParams.searchString;
  clearExistingSingleValueFilters(searchString, filters.defaultFilters, !appState.features.isFeatureEnabled('disableSearchFilterParsingV2'));
  const filterSearchString = serializeFilters(filters, allMdfs, searchParams.dateTimeSearch);
  const remainingFilterQueryParams = getFilterQueryParams(filters);

  if (searchString.length === 0 && filterSearchString.length > 0)
    finalSearchString = `${filterSearchString}`;
  else if (searchString.length > 0 && filterSearchString.length > 0)
    finalSearchString = `${searchString} ${filterSearchString}`;
  else
    finalSearchString = searchString;

  searchState.ops.setRawSearchString(finalSearchString);

  const routerLinkQueryParams: Record<string, string> = {
    searchString: finalSearchString,
    viewType: currentViewType,
    isFuzzy: searchParams.isFuzzy ? 'true' : 'false',
    atSameTime: searchParams.atSameTime ? 'true' : 'false',
    defaultDateRangeField: searchParams.defaultDateRangeField,
    ts: new Date().getTime().toString(),
    useCustomSearchHook: searchParams.useCustomSearchHook ? 'true' : 'false',
    allTypes: searchState.reactive.allTypesFilter ? 'true' : 'false',
    ...remainingFilterQueryParams,
  };

  if (searchParams.defaultDateRangeFormId) {
    routerLinkQueryParams.defaultDateRangeFormId = searchParams.defaultDateRangeFormId;
  }

  if (route && route.name === 'recycleBin') {
    routerLinkQueryParams.showOnlyDeletedItems = route.query.showOnlyDeletedItems as string;
  }

  if ('folderId' in remainingFilterQueryParams && includeSubfolders) {
    routerLinkQueryParams.includeSubfolders = 'true';
  }

  if (getGlobalRouter().currentRoute?.name === 'browse') {
    addMosParamsInQueryParams(routerLinkQueryParams);
  }

  return routerLinkQueryParams;
};

export const prepareSearchRouterQueryParamsForVersion = (versionId: string) => {
  const routerLinkQueryParams: Record<string, string> = {
    searchString: '',
    viewType: ViewType.LIST,
    isFuzzy: 'false',
    atSameTime: 'false',
    ts: new Date().getTime().toString(),
    versionId: versionId,
  };

  if (getGlobalRouter().currentRoute?.name === 'browse') {
    addMosParamsInQueryParams(routerLinkQueryParams);
  }

  return routerLinkQueryParams;
};

/**
 * Persist MOS params in route when conducting any search operation.
 * So that MOS workflow works if user logout and login from same browser.
 */
const addMosParamsInQueryParams = (queryParams: Record<string, string>): Record<string, string> => {
  mosPersistedParams.forEach(param => {
    const value = getWhiteListedParamValue(param);
    if (value) {
      queryParams[param] = value;
    }
  });
  return queryParams;
};

export const getFieldsWithPrefix = (obj: Record<string, any>, prefix: string): Record<string, any> => {
  const fieldsWithPrefix: Record<string, string | string[]>[] = [];
  for (const key in obj) {
    if (key.startsWith(prefix)) {
      const temp = { [key]: obj[key] };
      fieldsWithPrefix.push(temp);
    }
  }
  return Object.assign({}, ...fieldsWithPrefix);
};

export const getFieldsWithoutPrefix = (obj: Record<string, any>, prefix: string) => {
  const fieldsWithOutPrefix: Record<string, any> = {};
  for (const key in obj) {
    if (!key.startsWith(prefix)) fieldsWithOutPrefix[key] = obj[key];
  }
  return fieldsWithOutPrefix;
};

export const addPrefixToFields = (obj: Record<string, any>, prefix: string) => {
  const fieldsWithPrefixAddded: Record<string, string[]> = {};
  for (const key in obj) {
    const keyWithPrefix = prefix + key;
    fieldsWithPrefixAddded[keyWithPrefix] = obj[key];
  }
  return fieldsWithPrefixAddded;
};

/**
   * Currently, the actual parsing of the filters are done _after_ pushing it to the search through URL.
   *
   * To ensure that a filter can only have one value always, we clear the current filter value in memory
   * if we detect that the incoming search string contains at least one value for a single value filter. This avoids the corner
   * case where a user types a different type in the searchbox than currently is active, which would result in
   * a search string with two key:value filters - we would not reliably know which one to pick when we load the search results
   * from the query parameters.
   *
   * We therefore clear the existing type filter if we detect an incoming single value filter.
   */
const clearExistingSingleValueFilters = (searchString: string, filters: DefaultFilters, useNewParsing: boolean) => {
  const separatedFilters = separateFiltersFromSearchString(searchString, useNewParsing);
  const singleValueFilters = Object.values(filters).filter(f => f.singleValue);
  for (const f of separatedFilters.parsedFilters) {
    if (f.isRecognized) {
      const matchingFilter = singleValueFilters.find(filter => filter.key === f.token);
      if (matchingFilter) {
        matchingFilter.clearValue();
      }
    }
  }
};

/** What the router hands back for one query key: a string, an array of strings/nulls when the
 *  key repeats, or undefined when it is absent. */
type RouteQueryValue = string | (string | null)[] | undefined;

/** Narrows a route query value to a single string. The URL boundary can hand back an array
 *  (repeated key) or a null (bare key), so collapse to the first usable string. So `?id=a&id=b`
 *  reads as 'a', and a bare `?id` reads as undefined. */
export function firstQueryValue(value: RouteQueryValue): string | undefined {
  const first = Array.isArray(value) ? value[0] : value;
  return first ?? undefined;
}

/**
 * Maps query string parameters to the state. If no query string is present then
 * sets state with default values.
 * @param formState
 * @param searchFormState
 * @param queryParams
 * @param isPerson
 */
export const mapQueryParamsToState = async (logger: Logger, searchState: SearchState, queryParams: Route['query']) => {
  const searchString = firstQueryValue(queryParams.searchString) ?? '';
  const defaultDateRangeFormId = firstQueryValue(queryParams.defaultDateRangeFormId);
  const defaultDateRangeField = firstQueryValue(queryParams.defaultDateRangeField);

  if (processAndSetMdfsPromise != null) {
    await processAndSetMdfsPromise;
    updateProcessAndSetMdfsPromise(null);
  }

  const store = getGlobalStore();
  const allSchemas = mdfState.state.mdfs;

  const filters: Filters = store.getters.filters;

  const separatedFilters = separateFiltersFromSearchString(searchString, !appState.features.isFeatureEnabled('disableSearchFilterParsingV2'));

  // Then we deal with loading a mdf if we find a formId
  const formIdFilter = separatedFilters.parsedFilters.find((f: ParsedFilter) => filterTypeForKey(f.token) === 'filter:formId');
  const defaultMdf = cloneDeep(mdfState.state.defaultMdf);
  invariant(defaultMdf, 'defaultMdf should be defined at this point');

  const schemasUsedInDefaultMdf = getMainAndSubtypeSchemas(allSchemas, defaultMdf.id);
  defaultMdf.fields =  schemasUsedInDefaultMdf.flatMap(s => s.fields ?? []);

  const possibleMdfs: MetaDataSchema[] = [];
  let mdfMatchingFormIdFilter: MetaDataSchema | undefined = undefined;
  if (formIdFilter) {
    try {
      const possibleMdf = cloneDeep(allSchemas.find(s => s.id === formIdFilter.value || s.label === formIdFilter.value));
      if (possibleMdf) {
        mdfMatchingFormIdFilter = possibleMdf;
        const schemas = getMainAndSubtypeSchemas(allSchemas, possibleMdf.id).filter(s => s.id !== (defaultMdf?.id || DEFAULT_MDF_ID));
        possibleMdfs.push(...schemas);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        showToast(`MDF with label '${formIdFilter.value}' does not exist.`, TOAST.INFO);
      } else {
        logErrorAndShowToast('MDF search failed: ' + exceptionSummary(err), err, 'ex in mapQueryParamsToState');
      }
      formIdFilter.isRecognized = false;
    }
  }

  const possibleMdfIds = new Set(possibleMdfs.flatMap(mdf => [mdf.id, mdf.label]));

  const dateFilterFormIds = new Set<string>(([filters.dateFilter.metadataFormId, defaultDateRangeFormId]).filter((id): id is string => Boolean(id)));
  for (const formId of dateFilterFormIds) {
    if (!possibleMdfIds.has(formId)) {
      const mdf = allSchemas.find(s => s.id === formId || s.label === formId);
      if (mdf) {
        possibleMdfs.push(mdf);
        possibleMdfIds.add(mdf.id);
        possibleMdfIds.add(mdf.label);
      }
    }
  }

  // Attempt to match unrecognized filters vs valid mdf custom fields
  const matchedCustomFilters = matchCustomFilters(logger, possibleMdfs, defaultMdf, separatedFilters.parsedFilters, store.state.allUsers, store.getters.optionLists, appState.dateHelper);
  const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);

  // Make sure the UI reacts to the changes properly using Vue.set
  setCustomFiltersToState(filters, matchedCustomFilters, vueSet);

  if (!appState.features.isFeatureEnabled('disableSearchFilterParsingV2')) {
    applyMultiValueOperators(searchString, allSchemas, filters);
    // A bookmarked or hand-edited URL can carry an impossible operator combination. It still
    // loads, but warns so the empty result is explained.
    const issue = validateOperators(searchString, allSchemas);
    if (issue) {
      showToast(describeOperatorIssue(issue), TOAST.WARNING);
    }
  }

  // If we have an mdf - update the formId filter manually.
  if (mdfMatchingFormIdFilter) {
    const defaultFormFilter = filters.defaultFilters['filter:formId'];
    // After introducing mdf displayName, we should show the displayName in the UI if it exists.
    defaultFormFilter.alias = mdfMatchingFormIdFilter.resolvedDisplayName;
    defaultFormFilter.parseValue(mdfMatchingFormIdFilter.id);
  }

  setDefaultDateRangeField(
    getGlobalStore(),
    searchState,
    {
      fieldIdOrGuid: defaultDateRangeField,
      formId: defaultDateRangeFormId
    }
  );
  searchState.ops.updateFromQueryParams(logger, filters, mdfState.state.mdfs, separatedFilters, queryParams);

  // Actually update the state for the UI for all recognized filters
  store.commit('updateFilters', separatedFilters.parsedFilters.filter((f: ParsedFilter) => f.isRecognized));

  /**
   * We can not apply both filters; itemId filter is special and will do a single GET on the provided item.
   * There's no guarantee that the itemId would be present in the provided folderId, so we simply do not
   * parse a folderId if an itemId is present, and clear the current folder, if any.
   */
  if ('itemId' in queryParams) {
    const itemId = firstQueryValue(queryParams.itemId);
    if (itemId !== undefined) {
      filters.defaultFilters['filter:itemId'].addValue(itemId);
    }
    filters.defaultFilters['filter:folderId'].clearValue();
    setCurrentFolder(null);
  } else if ('folderId' in queryParams) {
    const folderId = firstQueryValue(queryParams.folderId);
    if (folderId !== undefined) {
      filters.defaultFilters['filter:folderId'].addValue(folderId);
    }
  }
};

export interface SortByField {
  label: string;
  value: string;
  shouldDisableSortIfPersonSearch: boolean;
  isCustom?: boolean;
  fieldId?: string;
}

export const sortByFieldsLabelMap: Record<`${SortOptions}`, string> = {
  [SortOptions.RELEVANCE]: 'relevance',
  [SortOptions.ITEM_CREATED_DATE]: 'item created date',
  [SortOptions.MEDIA_CREATED_DATE]: 'media created date',
  [SortOptions.PLANNED_INGEST_TIME]: 'planned ingest time',
  [SortOptions.MODIFIED_ON]: 'modified date',
  [SortOptions.TITLE]: 'title',
  [SortOptions.MEDIA_DURATION]: 'duration',
  [SortOptions.MEDIA_SIZE]: 'size',
  [SortOptions.ITEM_DELETION_DATE]: 'item deletion date',
  [SortOptions.PINNED]: 'pinned',
};

/**
 * Returns all the sortable fields (default and custom)
 */
export const getSortableFields = (mdf: MdfEntity | null, routeName?: string | null): SortByField[] | [] => {
  const sortableFields: SortByField[] = [];
  const sortOptionskeys: (keyof typeof SortOptions)[] = <(keyof typeof SortOptions)[]>Object.keys(SortOptions);
  for (const key of sortOptionskeys) {
    if (routeName && routeName !== 'recycleBin' && key === 'ITEM_DELETION_DATE') continue;
    const sortByField: SortByField = {
      value: SortOptions[key],
      label: sortByFieldsLabelMap[SortOptions[key]],
      isCustom: false,
      shouldDisableSortIfPersonSearch: shouldDisableSortIfPersonSearch(SortOptions[key])
    };
    sortableFields.push(sortByField);
  }

  if (mdf?.fields && mdf?.views) {
    for (const field of mdf.fields) {
      if (field.sortable && field.type === FieldType.Date) {
        const customSortByField: SortByField = {
          value: field.id,
          label: field.fieldId,
          isCustom: true,
          shouldDisableSortIfPersonSearch: true,
          fieldId: field.fieldId
        };
        const fieldView = getViewDetailsByFieldId(mdf.views, field.fieldId, MdfViewType.ITEM);
        if (fieldView && !isEmpty(fieldView.label)) {
          const { label } = fieldView;
          customSortByField.label = label;
        }
        sortableFields.push(customSortByField);
        sortByFieldsLabelMap[customSortByField.value as SortOptions] = customSortByField.label;
      }
    }
  }

  return sortableFields;
};

const shouldDisableSortIfPersonSearch = (key: SortOptions) => {
  return (
    key === SortOptions.TITLE
    || key === SortOptions.MEDIA_DURATION
    || key === SortOptions.MEDIA_SIZE
    || key === SortOptions.MEDIA_CREATED_DATE
    || key === SortOptions.PLANNED_INGEST_TIME
    || key === SortOptions.MODIFIED_ON
    || key === SortOptions.PINNED
  );
};

export interface DefaultDateRangeFieldChoice {
  value: string;
  label: string;
  shouldDisableSortIfPersonSearch: boolean;
  customMdf?: MetaDataSchema;
}

export const mediaCreatedDateRangeField = { value: 'mediaCreatedOn', label: 'media created date', shouldDisableSortIfPersonSearch: true };

const createDateFilterMdfLabelSuffix = (mdf: MetaDataSchema) => ` (${mdf.resolvedDisplayName})`;

export const getDefaultDateRangeFieldChoices = (allMdfs: MetaDataSchema[] | null): DefaultDateRangeFieldChoice[] => {
  const defaultDateRangeFieldChoices: DefaultDateRangeFieldChoice[] = [
    { value: 'createdOn', label: 'item created date', shouldDisableSortIfPersonSearch: false },
    mediaCreatedDateRangeField,
    { value: 'scheduledRecording.startTimeMs', label: 'planned ingest time', shouldDisableSortIfPersonSearch: true },
    { value: 'modifiedOn', label: 'modified date', shouldDisableSortIfPersonSearch: true },
  ];

  if (!allMdfs) return defaultDateRangeFieldChoices;

  // To ensure that the fields from the default mdf are at the top of the list
  const allMdfsSorted = allMdfs?.sort((a, b) => {
    if (a.id === DEFAULT_MDF_ID) return -1;
    if (b.id === DEFAULT_MDF_ID) return 1;
    return 0;
  }) ?? [];

  const choicesIndexedByFieldId = new Map<string, DefaultDateRangeFieldChoice[]>();

  let defaultMdf: MetaDataSchema | undefined = undefined;
  for (const mdf of allMdfsSorted) {
    const isCustom = mdf.id !== DEFAULT_MDF_ID;
    if (!isCustom) defaultMdf = mdf;
    for (const field of mdf.fields) {
      if (field.type !== FieldType.Date || defaultDateRangeFieldChoices.find(choice => choice.value === field.id)) {
        continue;
      }

      const existingByFieldId = choicesIndexedByFieldId.get(field.fieldId);
      const fieldView = getViewDetailsByFieldId(mdf.views, field.fieldId, MdfViewType.ITEM);
      let label = fieldView?.label || field.fieldId;
      if (existingByFieldId && existingByFieldId.length > 0) {
        label += createDateFilterMdfLabelSuffix(mdf);
        for (const existing of existingByFieldId) {
          const sourceMdf = existing.customMdf ?? defaultMdf;
          if (!sourceMdf) continue;
          const labelSuffix = createDateFilterMdfLabelSuffix(sourceMdf);
          if (!existing.label.endsWith(labelSuffix)) existing.label += labelSuffix;
        }
      }

      const customDateField: DefaultDateRangeFieldChoice = {
        value: field.id,
        label,
        shouldDisableSortIfPersonSearch: true,
        customMdf: isCustom ? mdf : undefined,
      };
      defaultDateRangeFieldChoices.push(customDateField);
      if (existingByFieldId) {
        existingByFieldId.push(customDateField);
      } else {
        choicesIndexedByFieldId.set(field.fieldId, [customDateField]);
      }
    }
  }

  return defaultDateRangeFieldChoices;
};

export enum SearchType {
  /**
   * Regular search results, when no folder is selected.
   */
  SEARCH_VIEW = 'search_view',
  /**
   * Items + folders in currently selected folder.
   */
  FOLDERS_VIEW = 'folder_view',
  /**
   * Filters in currently selected folder.
   * (by searching anything without selecting 'search in all sub folders'.)
   */
  FOLDER_FILTER = 'folder_filter',
}

export const getCurrentSearchType = () => {
  const store = getGlobalStore();
  const includeSubfolders = store.getters.includeSubfolders as boolean;
  const rawSearchString = searchState.reactive.searchForm.rawSearchString;
  const currentFolder = folders.currentFolder;

  if (currentFolder && !includeSubfolders && !rawSearchString) return SearchType.FOLDERS_VIEW;
  if (currentFolder && rawSearchString && !includeSubfolders) return SearchType.FOLDER_FILTER;

  return SearchType.SEARCH_VIEW;
};


export function resetDateFilterIfCustom(filters: Filters) {
  if (filters.dateFilter.metadataFormId !== DEFAULT_MDF_ID) {
    setDefaultDateRangeField(
      getGlobalStore(),
      searchState,
      { fieldIdOrGuid: mediaCreatedDateRangeField.value, formId: undefined }
    );
  }
}

export const mediaPreviewCollapseTooltip = (isCollapsed: boolean, itemType: SearchItemType): string => {
  if (itemType === ItemType.VIDEO || itemType === ItemType.AUDIO || itemType === ItemType.IMAGE || itemType === ItemType.FILE) {
    return isCollapsed ? 'Show media preview' : 'Collapse media preview';
  }
  return '';
};
